// ── HOURS INVOICE EDITOR ────────────────────────────────────────────────
// Presentational component for the per-category Toggl breakdown +
// adjustment notes + Claude narratives + invoice-preview frame.
//
// Two callers:
//   1. DesignerHoursSection — the live draft-edit flow. Renders with
//      readOnly=false (default). Inputs editable, action buttons
//      visible (Save Draft / Summarize / Regenerate / Consolidate /
//      Generate PDF).
//   2. DesignerHoursSection inspection modal — opens when the user
//      clicks a finalized (Final/Partial/Paid) row in the Recent
//      Hours Invoices table. Renders with readOnly=true. Same JSX,
//      data shown identically; the three input types render
//      `disabled` (preserves layout) and the five action buttons are
//      hidden (cleans up the read-only surface).
//
// This component reads no state from context. Everything it needs is
// passed as props; the parent owns the state. That separation is what
// lets the same component serve the live editor and the inspection
// modal without state leakage between them.
//
// readOnly truth table:
//   - Hours input (per entry) → disabled
//   - Adjustment note input (per category) → disabled
//   - Narrative textarea (per category) → disabled
//   - Save Draft button → hidden
//   - "Summarize with Gilda" button (initial generate) → hidden
//   - "Regenerate" button (re-summarize) → hidden
//   - "Consolidate" button → hidden
//   - "Generate PDF" button → hidden

import React from 'react';

function fmtFull(n) {
  if (!n && n !== 0) return '—';
  return '$' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const inp = {
  padding: '7px 10px', border: '1px solid #ddd', borderRadius: 6,
  fontSize: 13, fontFamily: 'inherit', outline: 'none', background: '#fff',
  boxSizing: 'border-box',
};

export default function HoursInvoiceEditor(props) {
  const {
    effective,
    adjustedHours,
    adjustmentNotes,
    summaries,
    project,
    client,
    monthName,
    year,
    onAdjustedHoursChange,
    onAdjustmentNoteChange,
    onSummaryChange,
    onCopyRawEntries,
    onSaveDraft,
    onSummarizeCategory,
    onConsolidateSummary,
    onGeneratePdf,
    draftSaveStatus,
    draftSaveError,
    summarizingCat,
    consolidatingCat,
    pdfGenerating,
    pdfError,
    readOnly,
  } = props;

  const effCategoryData = (effective && effective.categories) || [];
  const effTotalHours   = (effective && effective.totalHours)  || 0;
  const effTotalAmount  = (effective && effective.totalAmount) || 0;
  const adj   = adjustedHours   || {};
  const notes = adjustmentNotes || {};
  const sums  = summaries       || {};

  return (
    <div>
      {/* Totals cards */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
        {[
          { label: 'Total Hours',  value: effTotalHours.toFixed(1) + ' hrs', color: '#333' },
          { label: 'Total Amount', value: fmtFull(effTotalAmount),           color: '#111' },
        ].map(card => (
          <div key={card.label} style={{ background: '#f5f5f5', borderRadius: 8, padding: '10px 18px', textAlign: 'center' }}>
            <div style={{ fontSize: 20, fontWeight: 800, color: card.color }}>{card.value}</div>
            <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>{card.label}</div>
          </div>
        ))}
      </div>

      {/* Save Draft — hidden under readOnly */}
      {!readOnly && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <button onClick={onSaveDraft} disabled={draftSaveStatus === 'saving'}
            style={{ padding: '6px 14px', background: '#1a1a1a', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: draftSaveStatus === 'saving' ? 'wait' : 'pointer', opacity: draftSaveStatus === 'saving' ? 0.7 : 1 }}>
            {draftSaveStatus === 'saving' ? 'Saving…' : draftSaveStatus === 'saved' ? '✓ Draft saved' : 'Save Draft'}
          </button>
          {draftSaveStatus === 'error' && <span style={{ fontSize: 12, color: '#c0392b' }}>{draftSaveError}</span>}
        </div>
      )}

      {effCategoryData.filter(cat => (cat.totalHours || 0) > 0).map(cat => {
        const catHasAdjustments = cat.effEntries.some(e => {
          const key = `${cat.name}__${e.togglName}`;
          return adj[key] != null && Number(adj[key]) !== e.hours;
        });
        return (
        <div key={cat.name} style={{ border: '1px solid #e0e0e0', borderRadius: 8, marginBottom: 14, overflow: 'hidden' }}>
          <div style={{ background: '#f5f5f5', padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontWeight: 700, fontSize: 13 }}>{cat.name}</div>
            <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
              <span style={{ fontSize: 12, color: '#555' }}>{cat.effHours.toFixed(1)} hrs</span>
              <span style={{ fontSize: 13, fontWeight: 700, color: '#111' }}>{fmtFull(cat.effAmount)}</span>
              {sums[cat.name] && <span style={{ fontSize: 11, color: '#1A5C38', fontWeight: 600 }}>✓ Summary ready</span>}
            </div>
          </div>

          <div style={{ padding: '0 14px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                  {['Name', 'Role', 'Hours', 'Rate', 'Amount'].map(h => (
                    <th key={h} style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 600, color: '#888', fontSize: 11 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {cat.effEntries.map((entry, i) => {
                  const key = `${cat.name}__${entry.togglName}`;
                  const isAdjusted = adj[key] != null && Number(adj[key]) !== entry.hours;
                  return (
                    <tr key={i} style={{ borderBottom: '1px solid #f8f8f8' }}>
                      <td style={{ padding: '6px 8px' }}>{entry.togglName}</td>
                      <td style={{ padding: '6px 8px', color: '#666' }}>{entry.role}</td>
                      <td style={{ padding: '6px 8px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <input
                            type="number"
                            step="0.1"
                            min="0"
                            disabled={!!readOnly}
                            data-testid={'hours-input-' + key}
                            value={adj[key] != null ? adj[key] : entry.hours}
                            onChange={e => {
                              if (readOnly) return;
                              const val = e.target.value === '' ? null : parseFloat(e.target.value);
                              if (onAdjustedHoursChange) onAdjustedHoursChange(key, val);
                            }}
                            style={{ width: 70, textAlign: 'right', border: '0.5px solid #E0E0DC', borderRadius: 4, fontSize: 13, padding: '4px 6px', fontFamily: 'inherit', background: readOnly ? '#f7f7f5' : '#fff', color: readOnly ? '#555' : '#111' }}
                          />
                          {isAdjusted && (
                            <span style={{ fontSize: 11, color: '#aaa' }}>Toggl: {entry.hours.toFixed(1)}</span>
                          )}
                        </div>
                      </td>
                      <td style={{ padding: '6px 8px', color: '#666' }}>${entry.rate}/hr</td>
                      <td style={{ padding: '6px 8px', fontWeight: 600, color: '#111' }}>{fmtFull(entry.effAmount)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {/* Raw Toggl entries — visible on screen (2026-08-23; before
                this the comments only surfaced via Summarize or the
                clipboard copy, which read as "no details" in person
                mode). Collapsed by default; date · hours · comment. */}
            {(() => {
              const raw = (cat.effEntries || []).flatMap(e =>
                (e.timeEntries || []).map(t => ({ ...t, who: e.togglName })));
              return (
                <details data-testid={'raw-entries-' + cat.name} style={{ margin: '6px 0 4px' }}>
                  <summary style={{ fontSize: 11, color: '#888', cursor: 'pointer' }}>
                    Raw Toggl entries ({raw.length})
                  </summary>
                  {raw.length === 0 ? (
                    <div style={{ fontSize: 11, color: '#aaa', fontStyle: 'italic', padding: '6px 0 2px 12px' }}>
                      No time-entry comments came through the pull for this row.
                    </div>
                  ) : (
                    <div style={{ padding: '6px 0 2px 12px' }}>
                      {raw.map((t, j) => (
                        <div key={j} style={{ fontSize: 11, color: '#555', lineHeight: 1.6 }}>
                          <span style={{ color: '#aaa' }}>{t.date || '—'} · {((t.duration || 0) / 3600).toFixed(2)}h</span>
                          {' — '}{t.description || '(no note)'}
                        </div>
                      ))}
                    </div>
                  )}
                </details>
              );
            })()}
            {catHasAdjustments && (
              <div style={{ padding: '8px 0 10px' }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: '#555', display: 'block', marginBottom: 4 }}>Adjustment note (internal):</label>
                <input
                  type="text"
                  disabled={!!readOnly}
                  data-testid={'adjustment-note-' + cat.name}
                  placeholder="e.g. 8 hrs carried from March — Tim banked hours"
                  value={notes[cat.name] || ''}
                  onChange={e => {
                    if (readOnly) return;
                    if (onAdjustmentNoteChange) onAdjustmentNoteChange(cat.name, e.target.value);
                  }}
                  style={{ ...inp, width: '100%', fontSize: 12, background: readOnly ? '#f7f7f5' : '#fff', color: readOnly ? '#555' : '#111' }}
                />
              </div>
            )}
          </div>

          <div style={{ padding: '10px 14px', borderTop: '1px solid #f0f0f0' }}>
            {/* The textarea ALWAYS renders (2026-08-05 — pre-fix it only
                appeared once a summary existed, so a silently-empty
                summarize left no box at all, and pasting a narrative from
                an external chat required summarizing first). */}
            {(
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#555', marginBottom: 6 }}>Billing Narrative
                  <span style={{ fontWeight: 400, color: '#888880', marginLeft: 8 }}>prints as written — plain lines stay paragraphs · "- " bullets · "## " headings · **bold**</span>
                </div>
                <textarea
                  value={sums[cat.name] == null ? '' : sums[cat.name]}
                  placeholder={'Paste your narrative here (prints as written), or Summarize with Gilda below.'}
                  disabled={!!readOnly}
                  data-testid={'summary-textarea-' + cat.name}
                  onChange={e => {
                    if (readOnly) return;
                    if (onSummaryChange) onSummaryChange(cat.name, e.target.value);
                  }}
                  rows={12}
                  style={{ ...inp, width: '100%', fontSize: 12, resize: 'vertical', minHeight: 200, background: readOnly ? '#f7f7f5' : '#fff', color: readOnly ? '#333' : '#111' }} />
                {!readOnly && (
                  <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                    {onCopyRawEntries && (
                      <button onClick={() => onCopyRawEntries(cat)}
                        title="Copy this category's raw Toggl entries — paste into your own Claude chat"
                        style={{ padding: '4px 10px', background: 'none', border: '1px solid #ddd', borderRadius: 5, fontSize: 11, cursor: 'pointer', color: '#555' }}>
                        ⧉ Copy raw entries
                      </button>
                    )}
                    <button onClick={() => onSummarizeCategory && onSummarizeCategory(cat)} disabled={summarizingCat === cat.name || consolidatingCat === cat.name}
                      style={{ padding: '4px 10px', background: 'none', border: '1px solid #ddd', borderRadius: 5, fontSize: 11, cursor: summarizingCat === cat.name ? 'wait' : 'pointer', color: '#555', opacity: (summarizingCat === cat.name || consolidatingCat === cat.name) ? 0.5 : 1 }}>
                      {summarizingCat === cat.name ? 'Summarizing…' : (sums[cat.name] ? 'Regenerate' : 'Summarize with Gilda')}
                    </button>
                    <button onClick={() => onConsolidateSummary && onConsolidateSummary(cat.name)} disabled={summarizingCat === cat.name || consolidatingCat === cat.name}
                      style={{ padding: '4px 10px', background: 'none', border: '1px solid #ddd', borderRadius: 5, fontSize: 11, cursor: consolidatingCat === cat.name ? 'wait' : 'pointer', color: '#555', opacity: (summarizingCat === cat.name || consolidatingCat === cat.name) ? 0.5 : 1 }}>
                      {consolidatingCat === cat.name ? 'Consolidating…' : 'Consolidate'}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        );
      })}

      {/* Invoice preview — shown when all populated categories have summaries */}
      {(() => {
        const catsWithHours = effCategoryData.filter(cat => (cat.totalHours || 0) > 0);
        const allHaveSummaries = catsWithHours.length > 0 && catsWithHours.every(cat => sums[cat.name]);
        if (!allHaveSummaries) return null;
        const invoiceCats = catsWithHours;
        const allAdjustments = [];
        invoiceCats.forEach(cat => {
          cat.effEntries.forEach(entry => {
            const key = `${cat.name}__${entry.togglName}`;
            if (adj[key] != null && Number(adj[key]) !== entry.hours) {
              allAdjustments.push({ catName: cat.name, entry, billedHrs: Number(adj[key]) });
            }
          });
        });
        return (
          <div style={{ border: '2px solid #1a1a1a', borderRadius: 10, padding: 24, marginTop: 8 }}>
            <div style={{ fontWeight: 800, fontSize: 16, letterSpacing: 1, marginBottom: 4 }}>CIMON STARR</div>
            <div style={{ fontSize: 12, color: '#555', marginBottom: 16 }}>Date: {monthName} {year}</div>
            {client && (
              <div style={{ fontSize: 12, marginBottom: 16 }}>
                <div style={{ fontWeight: 600, marginBottom: 2 }}>Bill To:</div>
                <div>{client.name}</div>
                {client.billingAddress && <div>{client.billingAddress}</div>}
                {(client.billingCity || client.billingState) && (
                  <div>{[client.billingCity, client.billingState, client.billingZip].filter(Boolean).join(', ')}</div>
                )}
              </div>
            )}
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, marginBottom: 16 }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #1a1a1a' }}>
                  <th style={{ padding: '6px 0', textAlign: 'left', fontWeight: 700 }}>Service</th>
                  <th style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 700 }}>Hours</th>
                  <th style={{ padding: '6px 0', textAlign: 'right', fontWeight: 700 }}>Amount</th>
                </tr>
              </thead>
              <tbody>
                {invoiceCats.map(cat => (
                  <tr key={cat.name} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: '10px 0', verticalAlign: 'top' }}>
                      <div style={{ fontWeight: 600, marginBottom: 4 }}>{cat.name}</div>
                      <div style={{ color: '#555', fontSize: 11, lineHeight: 1.5 }}>{sums[cat.name]}</div>
                    </td>
                    <td style={{ padding: '10px 8px', textAlign: 'right', verticalAlign: 'top' }}>{cat.effHours.toFixed(1)}</td>
                    <td style={{ padding: '10px 0', textAlign: 'right', verticalAlign: 'top', fontWeight: 600 }}>{fmtFull(cat.effAmount)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ borderTop: '2px solid #1a1a1a' }}>
                  <td colSpan={2} style={{ padding: '10px 8px 6px', fontWeight: 700, textAlign: 'right' }}>TOTAL</td>
                  <td style={{ padding: '10px 0 6px', fontWeight: 800, textAlign: 'right', fontSize: 14 }}>{fmtFull(effTotalAmount)}</td>
                </tr>
              </tfoot>
            </table>
            {!readOnly && (
              <button onClick={onGeneratePdf} disabled={pdfGenerating}
                style={{ padding: '8px 18px', background: '#1a1a1a', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: pdfGenerating ? 'wait' : 'pointer', opacity: pdfGenerating ? 0.7 : 1 }}>
                {pdfGenerating ? 'Generating…' : 'Generate PDF'}
              </button>
            )}
            {!readOnly && pdfError && <div style={{ color: '#c0392b', fontSize: 12, marginTop: 8 }}>{pdfError}</div>}
            {allAdjustments.length > 0 && (
              <div style={{ marginTop: 20, background: '#f5f5f5', borderRadius: 8, padding: '14px 16px' }}>
                <div style={{ fontWeight: 700, fontSize: 10, letterSpacing: '0.1em', color: '#888', textTransform: 'uppercase', marginBottom: 10 }}>
                  Adjustments — Internal Record
                </div>
                {allAdjustments.map((adjRow, i) => (
                  <div key={i} style={{ marginBottom: 8, fontSize: 11 }}>
                    <div style={{ color: '#444' }}>
                      {adjRow.entry.togglName} · {adjRow.catName}: Toggl {adjRow.entry.hours.toFixed(1)} hrs → Billed {adjRow.billedHrs.toFixed(1)} hrs
                    </div>
                    {notes[adjRow.catName] && (
                      <div style={{ color: '#888', marginTop: 2 }}>{notes[adjRow.catName]}</div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
}
